import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router";
import Linkbar from "../../../shared/components/Linkbar/Linkbar";
import {
  FixaOption,
  FixaSelect,
} from "../../../shared/wrappers/FixaSelect/FixaSelect";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { NotFound } from "../../../task-management/components/NotFound/NotFound";
import {
  MediaAboutFields,
  type MediaAboutFieldValues,
} from "../../components/MediaAboutFields/MediaAboutFields";
import { MediaDetailCarousel } from "../../components/MediaDetailCarousel/MediaDetailCarousel";
import { MediaPublicationInfo } from "../../components/MediaPublicationInfo/MediaPublicationInfo";
import { ProductCard } from "../../components/ProductCard/ProductCard";
import { TagList } from "../../components/TagList/TagList";
import styles from "../media-detail-page.module.css";
import {
  mainMessageMainActivityValue,
  MEDIA_TYPE_OPTIONS,
  mediaCards,
  mediaDetailProducts,
  reasonForChange,
} from "../mock-media";

const mapMediaCardToAboutFieldValues = (
  media?: (typeof mediaCards)[number]
): MediaAboutFieldValues => ({
  // TODO: Replace this mock mapper with launch media detail API fields when backend data is available.
  location: media?.location ?? "",
});

// Mirrors the "Life at home" activity taxonomy used across the Media Management POC.
const LIFE_AT_HOME_ACTIVITIES = [
  "Socialise",
  "Cook",
  "Wash & Clean",
  "Eat",
  "Hobby",
  "Store & Organise",
  "Play, Create & Learn",
  "Work",
  "Come & Go",
  "Sleep",
  "Personal Care",
  "Stay Healthy",
];

export const MediaDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const media = useMemo(() => mediaCards.find((card) => card.id === id), [id]);

  const [mediaTypeDraftsByMediaId, setMediaTypeDraftsByMediaId] = useState<
    Record<string, string>
  >({});
  const products = useMemo(
    () => (media ? mediaDetailProducts(media.id) : []),
    [media]
  );
  const mediaType = media
    ? (mediaTypeDraftsByMediaId[media.id] ?? media.mediaType)
    : "";

  const onMediaTypeChange = (nextMediaType: string) => {
    if (!media) return;

    setMediaTypeDraftsByMediaId((currentDrafts) => ({
      ...currentDrafts,
      [media.id]: nextMediaType,
    }));
  };

  const mediaAboutInitialValues = useMemo(
    () => mapMediaCardToAboutFieldValues(media),
    [media]
  );
  const [mediaAboutDraftsByMediaId, setMediaAboutDraftsByMediaId] = useState<
    Record<string, MediaAboutFieldValues>
  >({});

  const mediaAboutValues = useMemo(
    () => ({
      ...mediaAboutInitialValues,
      ...(media ? mediaAboutDraftsByMediaId[media.id] : undefined),
    }),
    [media, mediaAboutDraftsByMediaId, mediaAboutInitialValues]
  );

  const onMediaAboutChange = (nextValues: MediaAboutFieldValues) => {
    if (!media) return;

    setMediaAboutDraftsByMediaId((currentDrafts) => ({
      ...currentDrafts,
      [media.id]: nextValues,
    }));
  };

  const [activityTags, setActivityTags] = useState(LIFE_AT_HOME_ACTIVITIES);
  const [selectedActivityTags, setSelectedActivityTags] = useState<string[]>(
    []
  );
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");

  const toggleActivityTag = (tag: string) => {
    setSelectedActivityTags((current) =>
      current.includes(tag)
        ? current.filter((selected) => selected !== tag)
        : [...current, tag]
    );
  };

  const handleAddTag = () => {
    const trimmedTag = newTagValue.trim();

    if (trimmedTag && !activityTags.includes(trimmedTag)) {
      setActivityTags((current) => [...current, trimmedTag]);
      setSelectedActivityTags((current) => [...current, trimmedTag]);
    }

    cancelAddTag();
  };

  const cancelAddTag = () => {
    setNewTagValue("");
    setIsAddingTag(false);
  };

  const linkbarItems = useMemo(
    () => [{ label: t("capitalCaseLaunchMediaString"), to: "/launch-media" }],
    [t]
  );

  const enableLaunchMedia = useFlag(FLAGS.ENABLE_LAUNCH_MEDIA);
  if (!enableLaunchMedia) {
    return <Navigate to="/home" replace />;
  }

  if (!media) {
    return (
      <main className={styles["page"]}>
        <NotFound
          title={t("mediaNotFoundString")}
          message={t("noResultsString")}
        />
      </main>
    );
  }

  const images = media.images?.length ? media.images : [media.imageUrl];

  return (
    <main className={styles["page"]}>
      <Linkbar items={linkbarItems} currentLabel={media.title} currentAsH1 />

      <section className={styles["pageContent"]}>
        <MediaDetailCarousel
          images={images}
          title={media.title}
          imageAlt={media.imageAlt}
        />

        <FixaText tagName="h2" className={styles["mediaTitle"]}>
          {t("workBriefString")}
        </FixaText>

        <div className={styles["field"]}>
          <FixaInputField
            id={"name"}
            type={"text"}
            value={media.title}
            readOnly
            label={t("nameString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaSelect
            id="mediaTypeSelect"
            label={t("selectMediaTypeString")}
            value={mediaType}
            onChange={(e) => onMediaTypeChange(e.target.value)}
          >
            {MEDIA_TYPE_OPTIONS.map((option) => (
              <FixaOption key={option} value={option} name={option} />
            ))}
          </FixaSelect>
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("reasonForChangeString")}
          </FixaText>

          <FixaTextArea
            id={"reasonForChange"}
            value={reasonForChange}
            readOnly
            label={t("whyMediaNeedsToBeChangedString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("mainMessageMainActivityString")}
          </FixaText>

          <FixaTextArea
            className={styles["mainMessageMainActivityComponent"]}
            id={"mainMessageMainActivity"}
            value={mainMessageMainActivityValue}
            readOnly
            label={t("specificNeedToBeSolvedString")}
          />
        </div>

        <MediaAboutFields
          mediaType={mediaType}
          location={media.location}
          values={mediaAboutValues}
          onChange={onMediaAboutChange}
        />

        <section className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("lifeHomeActivityString")}
          </FixaText>

          <TagList
            tags={activityTags}
            selectedTags={selectedActivityTags}
            onToggleTag={toggleActivityTag}
            isAddingTag={isAddingTag}
            newTagValue={newTagValue}
            onNewTagValueChange={setNewTagValue}
            onStartAddTag={() => setIsAddingTag(true)}
            onConfirmAddTag={handleAddTag}
            onCancelAddTag={cancelAddTag}
          />
        </section>

        <section className={styles["field"]}>
          <FixaText tagName="h2" className={styles["title"]}>
            {t("product2String")}
          </FixaText>

          <div className={styles["productsGrid"]}>
            {products.map(({ item, article, badges, saleStartDate }) => (
              <ProductCard
                key={article.id}
                item={item}
                article={article}
                badges={badges}
                saleStartDate={saleStartDate}
              />
            ))}
          </div>
        </section>

        <MediaPublicationInfo
          published={media.status === "Published"}
          publishedDate={media.publishedDate}
          onPublishToggle={() => {}}
        />
      </section>
    </main>
  );
};
